/**
 * AdminLayout Component
 *
 * Admin console shell with dark sidebar navigation.
 * Restricts access to admin role users only.
 */

import React, { ReactNode } from 'react';
import { Navigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts';
import { ADMIN_EMAIL } from '@/config/admin';
import { AdminNav } from '../components';

interface AdminLayoutProps {
  children: ReactNode;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    );
  }

  // Nobody signed in: send them to the login page (and come back after).
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Single-admin lock. The real gate is being AUTHENTICATED as the one
  // allowlisted admin email — nobody else can sign in as it, and every admin
  // data operation is independently enforced by firestore.rules isAdmin()
  // server-side. The Firestore `role`/`isAdmin` fields are only advisory here:
  // `role` doubles as the guest/host view mode (Become a Host flips it), and
  // the profile read can transiently fail and fall back to role 'guest' with
  // no isAdmin — both of which were locking the true admin out of the console
  // even though the server would have served the data. So grant the console on
  // the verified allowlisted email; treat role/isAdmin as informational.
  const emailMatches = user.email?.toLowerCase() === ADMIN_EMAIL;
  const roleMatches = user.role === 'admin' || user.isAdmin === true;
  const isAdmin = emailMatches;

  // A signed-in user who fails the check used to be bounced to "/" with no
  // explanation, which made every failure mode (wrong account, profile doc
  // that failed to load and fell back to role 'guest', stale session) look
  // identical and impossible to debug. Show what actually failed instead.
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
          <h1 className="text-lg font-semibold text-gray-900">Not authorised</h1>
          <p className="mt-2 text-sm text-gray-600">
            This account can&apos;t open the admin console.
          </p>
          <dl className="mt-4 text-left text-sm bg-gray-50 rounded-lg p-4 space-y-1">
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Signed in as</dt>
              <dd className="text-gray-900 break-all">{user.email || '(no email)'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Role</dt>
              <dd className="text-gray-900">{user.role || '(none)'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Admin flag</dt>
              <dd className="text-gray-900">{String(user.isAdmin === true)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">User id</dt>
              <dd className="text-gray-900 break-all text-xs">{user.uid}</dd>
            </div>
            {/* Build marker: if this line is absent, the browser is running a
                stale cached bundle and none of the current logic applies. */}
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Build</dt>
              <dd className="text-gray-900">admin-v2</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Failed check</dt>
              <dd className="text-gray-900">
                {user.profileLoadFailed
                  ? 'profile failed to load'
                  : !emailMatches && !roleMatches
                    ? 'email + role'
                    : !emailMatches
                      ? 'email'
                      : 'role'}
              </dd>
            </div>
          </dl>
          <p className="mt-4 text-xs text-gray-500">
            If this is the admin account, sign out and sign back in — the profile
            may have failed to load.
          </p>
          <div className="mt-5 flex items-center justify-center gap-3">
            <Link
              to="/"
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Back to site
            </Link>
            <Link
              to="/login"
              className="px-4 py-2 text-sm rounded-lg bg-gray-900 text-white hover:bg-gray-800"
            >
              Switch account
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Top Bar */}
      <header className="bg-gray-900 text-white sticky top-0 z-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="flex items-center gap-2">
                <img
                  src="/logos/logo-wide.png"
                  alt="DARAK"
                  className="h-7 w-auto"
                />
              </Link>
              <span className="text-sm text-gray-400 hidden sm:inline border-l border-gray-700 pl-4">
                Admin Console
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="text-sm text-gray-300 hover:text-white transition-colors"
              >
                View Site
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user.displayName?.charAt(0).toUpperCase() || 'A'}
                  </span>
                </div>
                <span className="text-sm text-gray-300 hidden sm:inline">
                  {user.displayName || user.email}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-gray-800 text-white min-h-[calc(100vh-64px)] sticky top-16 hidden md:block">
          <AdminNav />
        </aside>

        {/* Mobile sidebar toggle - shown on mobile */}
        <div className="md:hidden fixed bottom-4 left-4 z-50">
          <MobileNavTrigger />
        </div>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-8 min-h-[calc(100vh-64px)]">
          {children}
        </main>
      </div>
    </div>
  );
};

// Mobile navigation trigger component
const MobileNavTrigger: React.FC = () => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="p-3 bg-gray-800 rounded-full shadow-lg text-white"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Mobile sidebar overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 top-0 bottom-0 w-64 bg-gray-800">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <span className="text-white font-semibold">Menu</span>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-white"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <AdminNav onNavigate={() => setIsOpen(false)} />
          </div>
        </div>
      )}
    </>
  );
};

export default AdminLayout;
